import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { FC, useState } from 'react';

import { InvoiceNote } from '~/data/schemas/invoice';
import { BUTTON_SX, CARD_SX } from '~/theme';
import { toDateFormatMMddyyyy } from '~/utils/format';

type NoteProps = {
  note: InvoiceNote;
};
const Note: FC<NoteProps> = ({ note }) => (
  <Grid container sx={{ padding: '4px 16px', display: 'block' }}>
    <Grid size={{ xs: 12 }} sx={{ fontSize: '14px', color: 'rgba(0,0,0,.6)' }}>
      {toDateFormatMMddyyyy(note.createdAt)}
    </Grid>
    <Grid size={{ xs: 12 }} sx={{ fontSize: '14px' }}>
      {note.author?.email === 'dummy@liftai.com' ? 'Lift AI' : (note.author?.email ?? 'Consultant')}{' '}
      commented:
    </Grid>
    <Grid
      size={{ xs: 12 }}
      sx={{
        fontSize: '14px',
        color: 'rgba(0,0,0,.6)',
        fontStyle: 'italic',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      &quot;{note.content}&quot;
    </Grid>
    <Divider />
  </Grid>
);

/**
 * The white card the page tabs stack on their grey ground (see InvoiceOverview).
 *
 * A function rather than a module-level object: spreading `CARD_SX` at module
 * scope reads `~/theme` while it may still be initializing and throws a TDZ
 * error ("Cannot access 'CARD_SX' before initialization"). Every other consumer
 * of these tokens reads them inside a component body, which runs after all
 * modules have settled.
 */
const pageCardSx = () =>
  ({
    ...CARD_SX,
    p: 2,
    bgcolor: 'background.paper',
  }) as const;

type NotesProps = {
  notes: InvoiceNote[];
  onAddNote: (note: string) => void;
};
export const Notes: FC<NotesProps> = ({ notes, onAddNote }) => {
  const [value, setValue] = useState('');

  const handleAddNote = (text: string) => {
    if (!text.trim()) return;
    onAddNote(text);
    setValue('');
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  // Full width on the grey ground the other page tabs use: the composer and the notes
  // are cards on it.
  return (
    <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0, overflowY: 'auto', bgcolor: 'grey.50' }}>
      <Stack spacing={2} sx={{ px: 3, py: 3 }}>
        <Box sx={pageCardSx()}>
          <Stack spacing={2}>
            <TextField
              id="add-comment-box"
              label="Leave a comment"
              value={value}
              onChange={handleOnChange}
              multiline
              rows={5}
              fullWidth
            />
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                sx={BUTTON_SX}
                disabled={!value.trim()}
                onClick={() => handleAddNote(value)}
              >
                Add
              </Button>
            </Box>
          </Stack>
        </Box>
        <Box sx={pageCardSx()}>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
            Notes ({notes.length})
          </Typography>
          {notes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No notes yet.
            </Typography>
          ) : (
            notes.map((note) => <Note key={note.id} note={note} />)
          )}
        </Box>
      </Stack>
    </Box>
  );
};
